import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Modal } from '@/components/Modal';
import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/queryKeys';
import { errorMessage } from '@/pages/sites/pmis/components/format';
import { COST_KINDS, showAmount } from '@/pages/sites/pmis/office/officeDraft';
import { officeKeys } from '@/pages/sites/pmis/office/queries';
import { useAmountUnit } from '@/pages/sites/pmis/office/useAmountUnit';

/** 승인 대기 한 줄 — (현장, 달, 비목) 하나(DOC-125 후속). 재시작 전 서버를 생각해 칸은 느슨하게 받는다. */
type PendingCostApproval = {
  siteId: string;
  siteName?: string | null;
  periodYm: string;
  costKind: string;
  amount: number;
  ledgerAmount?: number | null;
  difference?: number | null;
  details?: { trade: string; amount: number }[];
  expectedUpdatedAt?: string | null;
};

const pendingKey = ['office', 'cost-approvals', 'pending'] as const;

/**
 * 투입금액 승인 대기 창 (DOC-125 후속 · 사용자 결정 2026-10-20).
 *
 * <p>할일의 「원가 승인 대기 N건」 줄이 {@code /home?costApproval=1} 로 연다 — 투표 창({@code PollPrompt})과
 * 같은 자리다. 승인 부서는 원가 시트를 열지 못하거나 열 일이 없어서, 승인할 비목의 입력 금액과 같은 달
 * 원장 금액만 나란히 두고 대조한 뒤 그 자리에서 승인한다.
 *
 * <p>줄을 누르면 그 아래 공종별 내역과 [승인]이 펼쳐진다. 승인은 원가 시트의 승인과 같은 API 이고,
 * 화면이 읽은 뒤 금액이 바뀌었으면 서버가 409 로 답한다.
 */
export function CostApprovalPrompt() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('costApproval') != null;
  if (!requested) return null;

  const close = () =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('costApproval');
        return next;
      },
      { replace: true },
    );

  return <CostApprovalModal onClose={close} />;
}

function CostApprovalModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [unit] = useAmountUnit();
  const [open, setOpen] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const pending = useQuery({
    queryKey: pendingKey,
    queryFn: () => apiFetch<PendingCostApproval[]>('/v1/pmis/office/cost-approvals/pending'),
    retry: false,
  });

  const approve = useMutation({
    mutationFn: (row: PendingCostApproval) =>
      apiFetch(`/v1/pmis/office/sites/${row.siteId}/months/${row.periodYm}/cost-approvals/${row.costKind}`, {
        method: 'POST',
        body: { expectedUpdatedAt: row.expectedUpdatedAt ?? null },
      }),
    onSuccess: (_data, row) => {
      setOpen(null);
      setMessage(null);
      void queryClient.invalidateQueries({ queryKey: pendingKey });
      void queryClient.invalidateQueries({ queryKey: queryKeys.todoItems });
      void queryClient.invalidateQueries({ queryKey: officeKeys.sheet(row.siteId) });
    },
    onError: (err) => {
      setMessage(errorMessage(err));
      void queryClient.invalidateQueries({ queryKey: pendingKey });
    },
  });

  const keyOf = (row: PendingCostApproval) => `${row.siteId}:${row.periodYm}:${row.costKind}`;
  const kindLabel = (kind: string) => COST_KINDS.find((item) => item.key === kind)?.label ?? kind;
  const rows = pending.data ?? [];

  return (
    <Modal title="원가 승인 대기" maxWidth="760px" onClose={onClose}>
      {message && (
        <p className="text-ui-sm" style={{ color: '#b91c1c', marginBottom: '8px' }}>
          {message}
        </p>
      )}
      {pending.isPending ? (
        <p className="text-ui-sm" style={{ color: 'var(--text-muted)' }}>
          불러오는 중입니다
        </p>
      ) : pending.isError ? (
        <p className="text-ui-sm" style={{ color: 'var(--text-muted)' }}>
          {errorMessage(pending.error)}
        </p>
      ) : rows.length === 0 ? (
        <p className="text-ui-sm" style={{ color: 'var(--text-muted)' }}>
          승인 대기가 없습니다.
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr>
                <th style={head}>현장</th>
                <th style={head}>월</th>
                <th style={head}>비목</th>
                <th style={{ ...head, textAlign: 'right' }}>입력</th>
                <th style={{ ...head, textAlign: 'right' }}>원장</th>
                <th style={{ ...head, textAlign: 'right' }}>차이</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const key = keyOf(row);
                const expanded = open === key;
                const differs = row.difference != null && row.difference !== 0;
                return (
                  <Fragment key={key}>
                    <tr
                      onClick={() => setOpen(expanded ? null : key)}
                      style={{
                        borderTop: '1px solid var(--border)',
                        cursor: 'pointer',
                        background: expanded ? 'var(--accent-bg)' : undefined,
                      }}
                    >
                      <td style={cell}>{row.siteName ?? ''}</td>
                      <td style={cell}>{row.periodYm}</td>
                      <td style={cell}>{kindLabel(row.costKind)}</td>
                      <td style={{ ...cell, textAlign: 'right' }}>{showAmount(row.amount, unit)}</td>
                      <td style={{ ...cell, textAlign: 'right' }}>{showAmount(row.ledgerAmount ?? null, unit)}</td>
                      <td style={{ ...cell, textAlign: 'right', color: differs ? '#b91c1c' : undefined }}>
                        {showAmount(row.difference ?? null, unit)}
                      </td>
                    </tr>
                    {expanded && (
                      <tr>
                        <td colSpan={6} style={{ ...cell, background: '#f8fafc', padding: '8px 12px' }}>
                          <div className="flex items-start justify-between gap-3">
                            <table style={{ borderCollapse: 'collapse', fontSize: '12px' }}>
                              <tbody>
                                {(row.details ?? []).map((line) => (
                                  <tr key={line.trade}>
                                    <td style={{ padding: '2px 12px 2px 0', color: 'var(--text-secondary)' }}>
                                      {line.trade === '' ? '공종 없음' : line.trade}
                                    </td>
                                    <td style={{ padding: '2px 0', textAlign: 'right' }}>
                                      {showAmount(line.amount, unit)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <button
                              type="button"
                              className="btn-primary"
                              disabled={approve.isPending}
                              onClick={(event) => {
                                event.stopPropagation();
                                approve.mutate(row);
                              }}
                            >
                              승인
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

const head = {
  padding: '6px 8px',
  fontSize: '12px',
  fontWeight: 700,
  color: 'var(--text-muted)',
  background: '#f8fafc',
  textAlign: 'left',
  whiteSpace: 'nowrap',
} as const;

const cell = { padding: '6px 8px', whiteSpace: 'nowrap', verticalAlign: 'top' } as const;
